import {
  Button,
  Checkbox,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { ChevronLeft, ChevronRight, CloseOutlined, DragIndicator } from '@mui/icons-material'
import { useState, useEffect } from 'react'
import { ALL_GSA_COLUMNS, GsaRow } from './GsaMasCatalog.types'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import '../EditQuote/LinesCustomView.css'

type Props = {
  open: boolean
  onClose: () => void
  visibleKeys: (keyof GsaRow)[]
  onSave: (keys: (keyof GsaRow)[]) => void
}

export default function EditDisplayFieldsDrawer({ open, onClose, visibleKeys, onSave }: Props) {
  const [selectedFields, setSelectedFields] = useState<(keyof GsaRow)[]>(visibleKeys)
  const [checkedAvailable, setCheckedAvailable] = useState<(keyof GsaRow)[]>([])
  const [checkedSelected, setCheckedSelected] = useState<(keyof GsaRow)[]>([])

  useEffect(() => {
    if (open) {
      setSelectedFields(visibleKeys)
      setCheckedAvailable([])
      setCheckedSelected([])
    }
  }, [open, visibleKeys])

  const availableFields = ALL_GSA_COLUMNS.filter(c => !selectedFields.includes(c.key))

  function moveToSelected() {
    setSelectedFields(prev => [...prev, ...checkedAvailable])
    setCheckedAvailable([])
  }

  function moveToAvailable() {
    setSelectedFields(prev => prev.filter(k => !checkedSelected.includes(k)))
    setCheckedSelected([])
  }

  function reset() {
    setSelectedFields(ALL_GSA_COLUMNS.map(c => c.key))
    setCheckedAvailable([])
    setCheckedSelected([])
  }

  function toggleAvailable(key: keyof GsaRow) {
    setCheckedAvailable(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]))
  }

  function toggleSelected(key: keyof GsaRow) {
    setCheckedSelected(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]))
  }

  function onDragEnd(result: DropResult) {
    if (!result.destination || result.destination.index === result.source.index) return
    const reordered = [...selectedFields]
    const [moved] = reordered.splice(result.source.index, 1)
    reordered.splice(result.destination.index, 0, moved)
    setSelectedFields(reordered)
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose} className="custom-view-drawer">
      <div className="custom-view-drawer-header">
        <Typography variant="h6">Edit Display Fields</Typography>
        <div className="custom-view-drawer-header-buttons">
          <Button variant="outlined" onClick={reset}>
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              onSave(selectedFields)
              onClose()
            }}
          >
            Save
          </Button>
          <IconButton size="small" onClick={onClose}>
            <CloseOutlined fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className="custom-view-drawer-content">
        <Table className="custom-view-drawer-table">
          <TableHead>
            <TableRow>
              <TableCell>Available Fields</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {availableFields.map(col => (
              <TableRow key={col.key}>
                <TableCell>
                  <Checkbox
                    size="small"
                    checked={checkedAvailable.includes(col.key)}
                    onChange={() => toggleAvailable(col.key)}
                  />
                  {col.label}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="custom-view-drawer-buttons">
          <IconButton onClick={moveToSelected} disabled={checkedAvailable.length === 0}>
            <ChevronRight />
          </IconButton>
          <IconButton onClick={moveToAvailable} disabled={checkedSelected.length === 0}>
            <ChevronLeft />
          </IconButton>
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <Table className="custom-view-drawer-table">
            <TableHead>
              <TableRow>
                <TableCell>Visible Fields</TableCell>
              </TableRow>
            </TableHead>
            <Droppable droppableId="selected-fields">
              {provided => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {selectedFields.map((key, index) => {
                    const col = ALL_GSA_COLUMNS.find(c => c.key === key)
                    if (!col) return null
                    return (
                      <Draggable key={key} draggableId={key} index={index}>
                        {provided => (
                          <TableRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={provided.draggableProps.style}
                            className="gsa-drawer-drag-row"
                          >
                            <TableCell className="gsa-drawer-drag-cell">
                              <DragIndicator fontSize="small" sx={{ color: 'grey.300' }} />
                              <Checkbox
                                size="small"
                                checked={checkedSelected.includes(key)}
                                onChange={() => toggleSelected(key)}
                              />
                              {col.label}
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    )
                  })}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </DragDropContext>
      </div>
    </Drawer>
  )
}
